import { supabase } from '../../config/supabase';
import logger from '../../utils/logger';
import { User, UserUpdateInput, RawUser } from '../../types/user';
import { resolveAppUrl } from '../../utils/resolveAppUrl';

/**
 * Service pour gérer les opérations CRUD sur les utilisateurs dans Supabase
 */
export const usersService = {
  /**
   * Récupère tous les utilisateurs
   * @returns Liste des utilisateurs
   */
  async getAllUsers(): Promise<User[]> {
    try {
      logger.info('Récupération de tous les utilisateurs');
      
      const { data, error } = await supabase
        .from('users')
        .select('*');
      
      if (error) {
        logger.error('Erreur lors de la récupération des utilisateurs:', error);
        throw error;
      }
      
      if (!data || data.length === 0) {
        logger.warn('Aucun utilisateur trouvé');
        return [];
      }
      
      return data.map(rawUser => this.toAppFormat(rawUser as RawUser));
    } catch (error) {
      logger.error('Exception lors de la récupération des utilisateurs:', error);
      return [];
    }
  },
  /**
   * Convertit un utilisateur brut de Supabase en utilisateur formaté pour l'application
   * @param rawUser Utilisateur brut de Supabase
   * @returns Utilisateur formaté pour l'application
   */
  toAppFormat(rawUser: RawUser): User {
    return {
      id: rawUser.id,
      agency_id: rawUser.agency_id,
      email: rawUser.email,
      name: rawUser.name,
      first_name: rawUser.first_name,
      photo_url: rawUser.photo_url,
      color: rawUser.color,
      role: rawUser.role as User['role'],
      is_owner: rawUser.is_owner || false,
      created_at: new Date(rawUser.created_at)
    };
  },

  /**
   * Récupère le profil utilisateur complet par son ID
   * @param userId ID de l'utilisateur
   * @returns Utilisateur ou null si non trouvé
   */
  async getUserProfile(userId: string): Promise<User | null> {
    try {
      logger.info(`Récupération du profil utilisateur pour l'ID: ${userId}`);
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          // Utilisateur non trouvé
          logger.warn(`Aucun profil utilisateur trouvé pour l'ID: ${userId}`);
          return null;
        }
        
        logger.error('Erreur lors de la récupération du profil utilisateur:', error);
        throw error;
      }
      
      if (!data) {
        return null;
      }
      
      return this.toAppFormat(data as RawUser);
    } catch (error: any) {
      logger.error('Exception lors de la récupération du profil utilisateur:', error);
      throw error;
    }
  },

  /**
   * Récupère les informations de l'agence associée à un utilisateur
   * @param agencyId ID de l'agence
   * @returns Informations de l'agence ou null si non trouvée
   */
  async getAgencyInfo(agencyId: string): Promise<{ id: string; name: string } | null> {
    try {
      logger.info(`Récupération des informations de l'agence pour l'ID: ${agencyId}`);
      
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name')
        .eq('id', agencyId)
        .single();
      
      if (error) {
        if (error.code === 'PGRST116') {
          // Agence non trouvée
          logger.warn(`Aucune agence trouvée pour l'ID: ${agencyId}`);
          return null;
        }
        
        logger.error('Erreur lors de la récupération des informations de l\'agence:', error);
        throw error;
      }
      
      return data;
    } catch (error: any) {
      logger.error('Exception lors de la récupération des informations de l\'agence:', error);
      throw error;
    }
  },

  /**
   * Met à jour le profil utilisateur
   * @param userId ID de l'utilisateur
   * @param userData Données à mettre à jour
   * @returns Utilisateur mis à jour
   */
  async updateUserProfile(userId: string, userData: UserUpdateInput): Promise<User> {
    try {
      logger.info(`Mise à jour du profil utilisateur pour l'ID: ${userId}`, userData);
      
      const { data, error } = await supabase
        .from('users')
        .update(userData)
        .eq('id', userId)
        .select()
        .single();
      
      if (error) {
        logger.error('Erreur lors de la mise à jour du profil utilisateur:', error);
        throw error;
      }
      
      return this.toAppFormat(data as RawUser);
    } catch (error: any) {
      logger.error('Exception lors de la mise à jour du profil utilisateur:', error);
      throw error;
    }
  },

  /**
   * Récupère tous les utilisateurs d'une agence
   * @param agencyId ID de l'agence (optionnel, utilise l'agence de l'utilisateur courant par défaut)
   * @returns Liste des utilisateurs de l'agence
   */
  async getUsersByAgency(agencyId?: string): Promise<{ data: User[] | null; error: any }> {
    try {
      // Si aucun agencyId n'est fourni, récupérer l'utilisateur courant
      if (!agencyId) {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData || !userData.user) {
          return { data: null, error: new Error('Utilisateur non authentifié') };
        }
        
        // Récupérer le profil de l'utilisateur pour obtenir son agency_id
        const userProfile = await this.getUserProfile(userData.user.id);
        if (!userProfile) {
          return { data: null, error: new Error('Profil utilisateur non trouvé') };
        }
        
        agencyId = userProfile.agency_id;
      }
      
      logger.info(`Récupération des utilisateurs pour l'agence: ${agencyId}`);
      
      // Récupérer tous les utilisateurs de l'agence avec une requête plus complète
      // Utiliser .or pour s'assurer de récupérer tous les utilisateurs, même ceux qui viennent d'être créés
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('agency_id', agencyId);
      
      if (error) {
        logger.error('Erreur lors de la récupération des utilisateurs de l\'agence:', error);
        return { data: null, error };
      }
      
      if (!data || data.length === 0) {
        logger.warn(`Aucun utilisateur trouvé pour l'agence: ${agencyId}`);
        return { data: [], error: null };
      }
      
      return { data: data.map(this.toAppFormat), error: null };
    } catch (error) {
      logger.error('Exception lors de la récupération des utilisateurs de l\'agence:', error);
      return { data: null, error };
    }
  },

  /**
   * Crée un nouvel utilisateur dans l'agence de l'utilisateur courant
   * @param userData Données de l'utilisateur à créer
   * @returns Résultat de l'opération
   */
  async createUser(userData: {
    email: string;
    password: string;
    name: string;
    first_name: string;
    role?: string;
    photo_url?: string;
  }): Promise<{ data: User | null; error: any }> {
    try {
      // Récupérer l'utilisateur courant
      const { data: authData } = await supabase.auth.getUser();
      if (!authData || !authData.user) {
        return { data: null, error: new Error('Utilisateur non authentifié') };
      }
      
      // Récupérer le profil de l'utilisateur pour obtenir son agency_id
      const userProfile = await this.getUserProfile(authData.user.id);
      if (!userProfile) {
        return { data: null, error: new Error('Profil utilisateur non trouvé') };
      }
      
      // Vérifier si l'utilisateur est propriétaire de l'agence
      if (!userProfile.is_owner) {
        return { data: null, error: new Error('Seul le propriétaire de l\'agence peut créer des utilisateurs') };
      }
      
      // Utiliser l'utilitaire resolveAppUrl pour déterminer l'URL de l'application
      const appUrl = resolveAppUrl();
      const emailRedirectTo = `${appUrl}/auth/callback`;
      
      // Logs conditionnels pour le développement uniquement
      if (process.env.NODE_ENV === 'development') {
        console.log('users.service - appUrl =', appUrl);
      }
      
      // Sauvegarder la session actuelle de l'admin
      const { data: currentSession } = await supabase.auth.getSession();
      const adminSession = currentSession?.session;
      
      // Créer le nouvel utilisateur dans Supabase Auth
      const { data: newUser, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          emailRedirectTo: emailRedirectTo,
          data: {
            name: userData.name,
            first_name: userData.first_name,
            agency_id: userProfile.agency_id,
            is_owner: false
          }
        }
      });
      
      if (authError || !newUser || !newUser.user) {
        logger.error('Erreur lors de la création de l\'utilisateur:', authError);
        return { data: null, error: authError || new Error('Erreur lors de la création de l\'utilisateur') };
      }
      
      // Restaurer immédiatement la session de l'admin
      if (adminSession) {
        await supabase.auth.setSession({
          access_token: adminSession.access_token,
          refresh_token: adminSession.refresh_token
        });
        logger.info('Session admin restaurée après création d\'utilisateur');
      }
      
      // Créer ou mettre à jour le profil utilisateur dans la table users
      // Certaines données sont déjà associées à l'utilisateur via les metadata, mais nous devons ajouter les champs supplémentaires
      const { error: profileError } = await supabase
        .from('users')
        .upsert({
          id: newUser.user.id,
          agency_id: userProfile.agency_id,
          email: userData.email,
          name: userData.name,
          first_name: userData.first_name,
          role: userData.role || 'user',
          photo_url: userData.photo_url || null,
          is_owner: false,
          created_at: new Date().toISOString()
        });
      
      if (profileError) {
        logger.error('Erreur lors de la création du profil utilisateur:', profileError);
        return { data: null, error: profileError };
      }
      
      // Récupérer l'utilisateur créé
      const createdUser = await this.getUserProfile(newUser.user.id);
      return { data: createdUser, error: null };
    } catch (error) {
      logger.error('Exception lors de la création de l\'utilisateur:', error);
      return { data: null, error };
    }
  },

  /**
   * Met à jour un utilisateur existant
   * @param userId ID de l'utilisateur à mettre à jour
   * @param userData Données à mettre à jour
   * @returns Résultat de l'opération
   */
  async updateUser(userId: string, userData: {
    name?: string;
    first_name?: string;
    role?: string;
    photo_url?: string;
  }): Promise<{ data: User | null; error: any }> {
    try {
      // Récupérer l'utilisateur courant
      const { data: authData } = await supabase.auth.getUser();
      if (!authData || !authData.user) {
        return { data: null, error: new Error('Utilisateur non authentifié') };
      }
      
      // Récupérer le profil de l'utilisateur courant
      const currentUserProfile = await this.getUserProfile(authData.user.id);
      if (!currentUserProfile) {
        return { data: null, error: new Error('Profil utilisateur non trouvé') };
      }
      
      // Récupérer le profil de l'utilisateur à mettre à jour
      const targetUserProfile = await this.getUserProfile(userId);
      if (!targetUserProfile) {
        return { data: null, error: new Error('Utilisateur cible non trouvé') };
      }
      
      // Vérifier si l'utilisateur courant est propriétaire de l'agence ou s'il modifie son propre profil
      if (!currentUserProfile.is_owner && currentUserProfile.id !== userId) {
        return { data: null, error: new Error('Vous n\'avez pas les droits pour modifier cet utilisateur') };
      }
      
      // Vérifier que les deux utilisateurs sont dans la même agence
      if (currentUserProfile.agency_id !== targetUserProfile.agency_id) {
        return { data: null, error: new Error('Vous ne pouvez pas modifier un utilisateur d\'une autre agence') };
      }
      
      // Mettre à jour le profil utilisateur
      const { error: updateError } = await supabase
        .from('users')
        .update({
          name: userData.name !== undefined ? userData.name : targetUserProfile.name,
          first_name: userData.first_name !== undefined ? userData.first_name : targetUserProfile.first_name,
          role: userData.role !== undefined ? userData.role : targetUserProfile.role,
          photo_url: userData.photo_url !== undefined ? userData.photo_url : targetUserProfile.photo_url
        })
        .eq('id', userId);
      
      if (updateError) {
        logger.error('Erreur lors de la mise à jour du profil utilisateur:', updateError);
        return { data: null, error: updateError };
      }
      
      // Récupérer l'utilisateur mis à jour
      const updatedUser = await this.getUserProfile(userId);
      return { data: updatedUser, error: null };
    } catch (error) {
      logger.error('Exception lors de la mise à jour de l\'utilisateur:', error);
      return { data: null, error };
    }
  },

  /**
   * Supprime un utilisateur
   * @param userId ID de l'utilisateur à supprimer
   * @returns Résultat de l'opération
   */
  async deleteUser(userId: string): Promise<{ success: boolean; error: any }> {
    try {
      // Récupérer l'utilisateur courant
      const { data: authData } = await supabase.auth.getUser();
      if (!authData || !authData.user) {
        return { success: false, error: new Error('Utilisateur non authentifié') };
      }
      
      // Récupérer le profil de l'utilisateur courant
      const currentUserProfile = await this.getUserProfile(authData.user.id);
      if (!currentUserProfile) {
        return { success: false, error: new Error('Profil utilisateur non trouvé') };
      }
      
      // Récupérer le profil de l'utilisateur à supprimer
      const targetUserProfile = await this.getUserProfile(userId);
      if (!targetUserProfile) {
        return { success: false, error: new Error('Utilisateur cible non trouvé') };
      }
      
      // Vérifier si l'utilisateur courant est propriétaire de l'agence
      if (!currentUserProfile.is_owner) {
        return { success: false, error: new Error('Seul le propriétaire de l\'agence peut supprimer des utilisateurs') };
      }
      
      // Vérifier que les deux utilisateurs sont dans la même agence
      if (currentUserProfile.agency_id !== targetUserProfile.agency_id) {
        return { success: false, error: new Error('Vous ne pouvez pas supprimer un utilisateur d\'une autre agence') };
      }
      
      // Vérifier que l'utilisateur à supprimer n'est pas le propriétaire
      if (targetUserProfile.is_owner) {
        return { success: false, error: new Error('Vous ne pouvez pas supprimer le propriétaire de l\'agence') };
      }
      
      // Supprimer l'utilisateur de la table users
      const { error: deleteProfileError } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);
      
      if (deleteProfileError) {
        logger.error('Erreur lors de la suppression du profil utilisateur:', deleteProfileError);
        return { success: false, error: deleteProfileError };
      }
      
      // Supprimer l'utilisateur de Supabase Auth
      const { error: deleteAuthError } = await supabase.auth.admin.deleteUser(userId);
      
      if (deleteAuthError) {
        logger.error('Erreur lors de la suppression de l\'utilisateur dans Auth:', deleteAuthError);
        return { success: false, error: deleteAuthError };
      }
      
      return { success: true, error: null };
    } catch (error) {
      logger.error('Exception lors de la suppression de l\'utilisateur:', error);
      return { success: false, error };
    }
  }
};

